import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormGroup,
  Validators
} from "@angular/forms";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {InputText} from 'primeng/inputtext';
import {Button} from 'primeng/button';
import {Password} from 'primeng/password';
import {InputOtp} from 'primeng/inputotp';
import {LoginService} from '../../core/services/login-service/login.service';
import {FooterComponent} from '../../shared/footer/footer.component';
import {LogoService} from '../../core/services/logo-service/logo.service';

@Component({
  selector: 'app-forget-password',
  templateUrl: './forget-password.component.html',
  styleUrls: ['./forget-password.component.css'],
  imports: [
    ReactiveFormsModule,
    RouterLink,
    InputText,
    Button,
    Password,
    InputOtp,
    FooterComponent
  ],
  providers: [LoginService],
  standalone: true
})
export class ForgetPasswordComponent implements OnInit {
  emailFormGroup: FormGroup;
  otpFormGroup: FormGroup;
  passwordFormGroup: FormGroup;
  errorMessage = '';
  @ViewChild('passwordInput') passwordInput: ElementRef<HTMLInputElement> | undefined;
  @ViewChild('faIconPasswordInputButton') faIconPasswordInputButton: ElementRef<HTMLElement> | undefined;
  @ViewChild('confirmPasswordInput') confirmPasswordInput: ElementRef<HTMLInputElement> | undefined;
  @ViewChild('faIconConfirmPasswordInputButton') faIconConfirmPasswordInputButton: ElementRef<HTMLElement> | undefined;
  emailLoader: boolean = false;
  otpLoader: boolean = false;
  passwordLoader: boolean = false;
  currentStep: number = 1;
  adminMode: boolean = false;
  logoUrl:string = ''
  constructor(
    private _formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private service: LoginService,
    private router: Router,
    private logoService: LogoService,
  ) {
    this.adminMode = this.route.snapshot.url.some(segment => segment.path.includes('admin'));
    this.emailFormGroup = this._formBuilder.group({email: ['', [Validators.required, Validators.pattern('[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}')]],});
    this.otpFormGroup = this._formBuilder.group({otp: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(8), Validators.pattern('^[0-9]*$')]],});
    this.passwordFormGroup = this._formBuilder.group({
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required],
    }, {validator: ConfirmedValidator('password', 'confirmPassword')});
  }

  ngOnInit(): void {
    this.logoService.logo$.subscribe((url) => {
      if (url) {
        this.logoUrl = url;
      }
    });
    const tokenString = localStorage.getItem('vendorClientUser_data');
    if (tokenString) {
      try {
        const token = JSON.parse(tokenString);
        if (token.access_token) {
          this.router.navigate(['/dashboard']);
          return;
        }
      } catch (error) {
        console.error('Invalid token on login page.');
      }
    }
    this.route.params.subscribe(res => {
      if (res['username'] && res['username'] != 'NA') {
        this.emailFormGroup.get('email')?.setValue(res['username']);
      }
    });
    this.emailFormGroup.get('email')?.valueChanges.pipe(
      debounceTime(100),
      distinctUntilChanged()
    ).subscribe(() => {
      this.handleEmailChange();
    });
  }

  sendOtpMail(): void {
    this.emailLoader = true;
    this.errorMessage = ''
    if (this.emailFormGroup.invalid) {
      this.emailLoader = false;
      if (this.emailFormGroup.get('email')?.errors) {
        if (this.emailFormGroup.get('email')?.errors?.['required']) {
          this.errorMessage = 'Email is required.';
        } else if (this.emailFormGroup.get('email')?.errors?.['pattern']) {
          this.errorMessage = 'Please enter a valid email address.';
        }
      }
      return;
    }
    this.service.sendResetMail(this.emailFormGroup.get('email')?.value).subscribe({
      next: () => {
        this.currentStep = 2;
      },
      error: (e) => {
        this.errorMessage = e.error?.reason;
      },
      complete: () => {
        this.emailLoader = false;
      },
    })
  }

  get otp() {
    return this.otpFormGroup.get('otp');
  }

  verifyOtp(): void {
    this.otpLoader = true;
    if (this.otpFormGroup.invalid) {
      this.otpLoader = false;
      if (this.otpFormGroup.get('otp')?.errors) {
        if (this.otpFormGroup.get('otp')?.errors?.['required']) {
          this.errorMessage = 'OTP is required.';
        } else if (this.otpFormGroup.get('otp')?.errors?.['pattern']) {
          this.errorMessage = 'Please enter a valid 8 digit OTP.';
        }
      }
      return;
    }
    this.errorMessage = '';
    this.service.checkResetPasswordToken(this.otpFormGroup.get('otp')?.value).subscribe({
      next: (res) => {
        if (res === 'valid') {
          this.currentStep = 3;
        } else {
          this.errorMessage = 'OTP is invalid';
        }
      },
      error: () => {
        this.errorMessage = 'An error occurred. Please try again later.';
      },
      complete: () => {
        this.otpLoader = false;
      }
    })
  }

  changePassword(): void {
    this.errorMessage = '';
    this.passwordLoader = true;
    if (this.passwordFormGroup.get('password')?.value !== this.passwordFormGroup.get('confirmPassword')?.value) {
      this.passwordLoader = false;
      this.errorMessage = 'Password and Confirm Password do not match.';
      return;
    }
    if (this.passwordFormGroup.invalid) {
      this.passwordLoader = false;
      this.errorMessage = 'Please fill out all fields correctly.';
      return;
    }
    const userType = this.adminMode ? 'ADMIN' : 'CLIENT';
    this.service.resetPassword(
      this.otpFormGroup.get('otp')?.value,
      this.passwordFormGroup.get('password')?.value,
      this.emailFormGroup.get('email')?.value, userType).subscribe({
      next: (updated) => {
        if (updated) {
          this.currentStep = 4;
        }
      },
      error: (e) => {
        if (e.error && e.error.reason) {
          this.errorMessage = e.error.reason;
        } else {
          this.errorMessage = 'An error occurred. Please try again later.';
        }
      },
      complete: () => {
        this.passwordLoader = false;
      }
    });
  }

  handleEmailChange(): void {
    const usernameControl = this.emailFormGroup.get('email');
    if (usernameControl?.touched || usernameControl?.dirty) {
      if (usernameControl?.invalid) {
        this.emailLoader = false;
        if (usernameControl.errors) {
          if (usernameControl.errors['required']) {
            this.errorMessage = 'Email is required.';
          } else if (usernameControl.errors['pattern']) {
            this.errorMessage = 'Please enter a valid email address.';
          }
        }
      } else {
        this.errorMessage = '';
      }
    }
  }
}

export function ConfirmedValidator(controlName: string, matchingControlName: string) {
  return (formGroup: UntypedFormGroup) => {
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];
    if (matchingControl.errors && !matchingControl.errors['confirmedValidator']) {
      return;
    }
    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({confirmedValidator: true});
    } else {
      matchingControl.setErrors(null);
    }
  }
}
